import React, {
  useState, useEffect, useRef, useMemo, useCallback,
} from "react";
import {
  ChevronLeft, Video, Phone, MoreVertical, Smile, Paperclip, Camera,
  Mic, Send, Lock, RotateCcw, Maximize2, Zap, AlertTriangle, CheckCheck, Info,
} from "lucide-react";
import { useBranding } from "../../contexts/BrandingContext";
import {
  advance, replyTo, matchTrigger, stepByKey,
} from "./flowSimEngine";

// ── Class strings ─────────────────────────────────────────────────────────
// Converted to Tailwind 2026-08-25 (Phase 5); flow-phone-preview.scss deleted
// with it, along with its 60-line `[data-theme="dark"]` block.
//
// ⚠️ THE SHEET'S HEADER SAID ITS LITERALS WERE DELIBERATE, AND IT WAS HALF RIGHT.
// The handset colours ARE Meta's and must not follow the institute's brand — a
// preview that turns violet stops being a preview. But that is TOKEN_CONTRACT §8
// (external brand chrome), not §8.1 (a sheet owning a second theme axis), and §8
// is explicit: those values live in `_brand-external.scss` as named tokens, "not
// inline at the call site". §8.1 says so too — "adding an entry needs a second
// theme axis, not an inconvenience".
//
// So the 22 Sass variables and ~60 loose hexes became one `--wa-client-*` set in
// `_brand-external.scss`, declared under `:root` and again under
// `:root[data-theme="dark"]`. Every rule below reads one token per ROLE, and the
// dark block is gone rather than ported — the token flipped underneath it. Where
// a fixed `--ext-whatsapp-*` token already held the value, the new one chains to
// it, so `grep '#efeae2'` still has exactly one answer.
//
// 🔴 AND ONE REAL BUG, four times. The tool chips outside the frame used
// `hsl(var(--accent))` for their hover border and their option numbers. `--accent`
// is `220 14% 95%` — a near-white SURFACE tint, one hyphen from `--accent-*`,
// exactly the trap tailwind.config.js documents. So the option number on every
// quick-reply chip has been near-white on a white card, and the hover border did
// not appear. It is `primary`.
//
// What stays OURS, deliberately: the simulator's own notes and the tools under
// the handset. A note is not a message and must never be mistaken for one, so
// its tones are our status tokens — and the sheet's dark values (#c4b5fd,
// #fcd34d, #fda4af) were already `--chart-1-fg`, `--warning-fg`, `--danger-fg`
// to the eye. It was writing our palette out by hand.

const FRAME =
  "tw-w-full tw-max-w-[292px] tw-rounded-[34px] tw-bg-[image:var(--wa-client-frame)] tw-p-[7px] " +
  "tw-shadow-[var(--wa-client-frame-shadow)]";

// Tall enough that a menu with several options and the reply under it are on
// screen together — a chat you have to scroll to read defeats the point of
// showing it as a chat. Bounded by the window because this sits in a sticky
// column: pinned and taller than the viewport, the composer and the quick
// replies underneath it would be unreachable, not merely cropped. The floor
// wins on the 1280×720 admin laptop, where nothing fits comfortably anyway.
const SCREEN =
  "tw-relative tw-flex tw-flex-col tw-overflow-hidden tw-rounded-[27px] " +
  "tw-bg-[var(--wa-client-chat)] " +
  "tw-font-[-apple-system,'Segoe_UI','Helvetica_Neue',sans-serif] " +
  "tw-h-[clamp(520px,calc(100vh-280px),630px)]";

// Capped against the viewport: the modal is centred, and a handset taller than
// the window would put the composer off screen.
const FRAME_FULL = FRAME.replace("tw-max-w-[292px]", "tw-max-w-[340px]");
const SCREEN_FULL = SCREEN.replace(
  "tw-h-[clamp(520px,calc(100vh-280px),630px)]",
  "tw-h-[min(680px,calc(100vh-200px))]",
);

const STATUS_BAR =
  "tw-flex tw-h-[26px] tw-items-center tw-justify-between tw-bg-[var(--wa-client-head)] " +
  "tw-px-[15px] tw-text-[0.62rem] tw-font-semibold tw-text-[var(--wa-client-on-head)]";

// Signal / wifi / battery, drawn rather than imported — three shapes are not
// worth an icon dependency, and lucide has no filled signal-bars glyph.
const SIG_BAR = "tw-w-0.5 tw-rounded-[1px] tw-bg-[var(--wa-client-on-head)]";

const BATT =
  "tw-relative tw-h-2 tw-w-[15px] tw-rounded-[2px] tw-border tw-border-solid " +
  "tw-border-[var(--wa-client-batt-line)] tw-bg-clip-content tw-p-px " +
  "tw-bg-[image:var(--wa-client-batt-fill)] " +
  "after:tw-absolute after:tw-right-[-3px] after:tw-top-0.5 after:tw-h-[3px] after:tw-w-0.5 " +
  "after:tw-rounded-[0_1px_1px_0] after:tw-bg-[var(--wa-client-batt-line)] after:tw-content-['']";

const TOP =
  "tw-flex tw-h-[50px] tw-items-center tw-gap-[9px] tw-bg-[var(--wa-client-head)] " +
  "tw-pe-[11px] tw-ps-[5px] tw-text-[var(--wa-client-on-head)] [&>svg]:tw-shrink-0 [&>svg]:tw-opacity-95";

const AVATAR =
  "tw-h-8 tw-w-8 tw-shrink-0 tw-rounded-full tw-bg-[var(--wa-client-avatar-bg)] tw-object-cover";

// The wallpaper sits behind the bubbles at low contrast, exactly as it does in
// the app — legibility of the message always wins. `> *` gets position:relative
// so the bubbles sit above the ::before tile.
const CHAT =
  "tw-relative tw-min-h-0 tw-flex-1 tw-overflow-y-auto tw-px-2 tw-pb-3 tw-pt-2.5 " +
  "tw-bg-[var(--wa-client-chat)] [scrollbar-width:thin] " +
  "before:tw-pointer-events-none before:tw-absolute before:tw-inset-0 before:tw-content-[''] " +
  "before:tw-bg-[image:var(--wa-client-doodle)] before:tw-bg-[length:260px] " +
  "before:tw-opacity-[var(--wa-client-doodle-opacity)] " +
  "before:[filter:var(--wa-client-doodle-filter)] " +
  "[&>*]:tw-relative " +
  "[&::-webkit-scrollbar]:tw-w-1 " +
  "[&::-webkit-scrollbar-thumb]:tw-rounded [&::-webkit-scrollbar-thumb]:tw-bg-[var(--wa-client-scroll)]";

const PILL =
  "tw-mx-auto tw-mb-2 tw-flex tw-w-fit tw-max-w-[92%] tw-items-center tw-justify-center " +
  "tw-gap-1 tw-rounded-[7px] tw-px-[9px] tw-py-1 tw-text-center tw-text-[0.6rem] " +
  "tw-font-medium tw-leading-[1.4] tw-shadow-[var(--wa-client-pill-shadow)]";

const DAY_PILL = `${PILL} tw-bg-[var(--wa-client-pill)] tw-text-[var(--wa-client-meta)]`;

const ENC_PILL =
  `${PILL} tw-mb-3 tw-bg-[var(--wa-client-enc-bg)] tw-text-[var(--wa-client-enc-ink)] ` +
  "[&>svg]:tw-shrink-0";

// The tail is an ::after that inherits the bubble's own background and is then
// clipped to a triangle — which is how it stays correct for both bubble colours
// without a second token. `[clip-path:…]` is an arbitrary PROPERTY, so it takes
// no tw- prefix; the `after:` variant still applies.
const BUBBLE =
  "tw-relative tw-min-w-[56px] tw-max-w-[82%] tw-rounded-sm tw-px-2 tw-pb-1 tw-pt-1.5 " +
  "tw-text-[0.755rem] tw-leading-[1.42] tw-text-[var(--wa-client-ink)] " +
  "tw-shadow-[var(--wa-client-bubble-shadow)] " +
  "after:tw-absolute after:tw-top-0 after:tw-h-[13px] after:tw-w-2 " +
  "after:tw-bg-[inherit] after:tw-content-['']";

const BUBBLE_IN =
  `${BUBBLE} tw-rounded-tl-none tw-bg-[var(--wa-client-in)] ` +
  "after:tw-left-[-7px] after:[clip-path:polygon(100%_0,100%_100%,0_0)]";

const BUBBLE_OUT =
  `${BUBBLE} tw-rounded-tr-none tw-bg-[var(--wa-client-out)] ` +
  "after:tw-right-[-7px] after:[clip-path:polygon(0_0,0_100%,100%_0)]";

const BUBBLE_TEXT =
  "tw-block tw-whitespace-pre-wrap tw-break-words " +
  "[&_code]:tw-rounded-[3px] [&_code]:tw-bg-[var(--wa-client-code-bg)] " +
  "[&_code]:tw-px-0.5 [&_code]:tw-text-[0.72rem] [&_code]:tw-text-[inherit]";

// Its own line under the text. WhatsApp tucks it beside the last line when one
// fits, but a numbered menu is always several lines — chasing that here would
// buy an edge case nobody previewing a menu will ever see.
const BUBBLE_META =
  "tw-mt-0.5 tw-flex tw-items-center tw-justify-end tw-gap-[3px] tw-whitespace-nowrap " +
  "tw-ps-2 tw-text-[0.6rem] tw-text-[var(--wa-client-meta)]";

const TYPING_BUBBLE =
  "tw-relative tw-flex tw-min-w-0 tw-items-center tw-gap-[3px] tw-rounded-sm " +
  "tw-rounded-tl-none tw-bg-[var(--wa-client-in)] tw-p-2.5 " +
  "tw-shadow-[var(--wa-client-bubble-shadow)]";

const TYPING_DOT =
  "tw-h-1.5 tw-w-1.5 tw-animate-typing-dot tw-rounded-full tw-bg-[var(--wa-client-typing)]";

// The simulator's own voice. Deliberately unlike a bubble — nothing in this
// style should ever be mistaken for something the customer receives.
const NOTE =
  "tw-mx-auto tw-mb-[7px] tw-flex tw-w-fit tw-max-w-[92%] tw-items-start tw-gap-[5px] " +
  "tw-rounded-[7px] tw-border tw-border-dashed tw-bg-[var(--wa-client-note-bg)] " +
  "tw-px-[9px] tw-py-[5px] tw-text-[0.63rem] tw-leading-[1.45] " +
  "[&>svg]:tw-mt-px [&>svg]:tw-shrink-0";

const NOTE_TONE = {
  info: `${NOTE} tw-border-[var(--wa-client-note-border)] tw-text-[var(--wa-client-note-ink)]`,
  action: `${NOTE} tw-border-chart-1/50 tw-text-chart-1-fg`,
  warn: `${NOTE} tw-border-warning/50 tw-text-warning-fg`,
  error: `${NOTE} tw-border-danger/50 tw-text-danger-fg`,
};

const COMPOSER =
  "tw-flex tw-items-center tw-gap-1.5 tw-bg-[var(--wa-client-panel)] tw-px-[7px] tw-py-1.5";

const COMPOSER_FIELD =
  "tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-gap-1.5 tw-rounded-[20px] " +
  "tw-bg-[var(--wa-client-field)] tw-px-[9px] tw-py-[5px] " +
  "tw-text-[var(--wa-client-field-ink)] [&>svg]:tw-shrink-0";

const COMPOSER_INPUT =
  "tw-min-w-0 tw-flex-1 tw-border-none tw-bg-transparent tw-text-[0.76rem] " +
  "tw-text-[var(--wa-client-ink)] tw-outline-none " +
  "placeholder:tw-text-[var(--wa-client-placeholder)]";

const SEND_BTN =
  "tw-flex tw-h-[34px] tw-w-[34px] tw-shrink-0 tw-cursor-pointer tw-items-center " +
  "tw-justify-center tw-rounded-full tw-border-none tw-bg-[var(--wa-client-head)] " +
  "tw-p-0 tw-text-[var(--wa-client-on-head)] hover:tw-brightness-110";

// ── Outside the frame: ours, not WhatsApp's ──
const TOOL_BTN =
  "tw-inline-flex tw-cursor-pointer tw-items-center tw-gap-1 tw-whitespace-nowrap " +
  "tw-rounded-[14px] tw-border tw-border-solid tw-border-line tw-bg-transparent " +
  "tw-px-2 tw-py-[3px] tw-text-[0.68rem] tw-text-content-muted " +
  "hover:tw-border-primary hover:tw-text-primary";

const STATE =
  "tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap tw-rounded-[14px] " +
  "tw-px-2 tw-py-[3px] tw-text-[0.67rem] tw-font-semibold";

const STATE_TONE = {
  live: `${STATE} tw-bg-success/[0.12] tw-text-success-fg`,
  done: `${STATE} tw-bg-[var(--elevate-1)] tw-text-content-muted`,
  bad: `${STATE} tw-bg-danger/[0.12] tw-text-danger-fg`,
};

const CHIP =
  "tw-inline-flex tw-max-w-full tw-cursor-pointer tw-items-center tw-gap-[5px] " +
  "tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap tw-rounded-[14px] " +
  "tw-border tw-px-[9px] tw-py-[3px] tw-text-[0.68rem]";

const CHIP_ON =
  `${CHIP} tw-border-solid tw-border-line tw-bg-surface-raised tw-text-content ` +
  "hover:tw-border-primary [&_b]:tw-text-[0.65rem] [&_b]:tw-text-primary";

const CHIP_OFF =
  `${CHIP} tw-border-dashed tw-border-line tw-bg-surface-raised tw-text-content-subtle ` +
  "hover:tw-border-warning hover:tw-text-warning-fg";

/**
 * FlowPhonePreview — runs the flow as WhatsApp would run it, on a phone.
 *
 * The old preview showed one step's text and told you, in prose, what a reply
 * would match. That answers "is this step worded right?" but not the question
 * that actually breaks flows in production: what does the whole conversation
 * feel like, three menus deep, on a phone? A dead-end option reads fine as a
 * form row and is obvious the moment a customer taps it and the chat goes
 * quiet — so the preview is the chat.
 *
 * Everything inside the frame is what the contact receives, byte for byte, in
 * send order (see flowSimEngine). Anything the simulator itself has to say —
 * an action it will not run, a step that no longer exists — is a note outside
 * the bubble stream, never dressed as a message. The frame's honesty is the
 * whole point of it.
 *
 * Actions are NEVER executed. `opt_out` from a preview would be a real opt-out.
 */

const uid = (() => { let n = 0; return () => `m${++n}`; })();

const clock = (d) => d
  .toLocaleTimeString([], { hour: "numeric", minute: "2-digit", hour12: true })
  .toLowerCase()
  .replace(/\s/g, " ");

/* WhatsApp's own inline formatting. Tokenised into React nodes rather than
   HTML — the text is admin-authored and must never reach innerHTML. */
const WA_TOKEN = /(```[\s\S]+?```)|(\*[^*\n]+\*)|(_[^_\n]+_)|(~[^~\n]+~)|(https?:\/\/\S+)/g;

const formatWa = (text) => {
  const out = [];
  let last = 0;
  let m;
  WA_TOKEN.lastIndex = 0;
  while ((m = WA_TOKEN.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const [raw, mono, bold, italic, strike, link] = m;
    const k = `t${m.index}`;
    if (mono) out.push(<code key={k}>{raw.slice(3, -3)}</code>);
    else if (bold) out.push(<strong key={k}>{raw.slice(1, -1)}</strong>);
    else if (italic) out.push(<em key={k}>{raw.slice(1, -1)}</em>);
    else if (strike) out.push(<s key={k}>{raw.slice(1, -1)}</s>);
    else if (link) {
      out.push(
        <span key={k} className="tw-text-[var(--wa-client-link)] tw-underline">{raw}</span>,
      );
    }
    last = m.index + raw.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
};

/* The signature of everything that changes what the contact would receive.
   Name, priority and expiry are left out on purpose: editing those mid-run is
   not a reason to make the admin start the conversation over. */
const runSignature = (flow) => JSON.stringify({
  s: (flow?.steps || []).map((st) => [
    st.key, st.message,
    (st.options || []).map((o) => [o.label, o.next, o.action, o.actionTargetId, o.reply]),
  ]),
  i: flow?.invalidReplyMessage,
  k: flow?.keywords,
  t: flow?.triggerType,
  b: flow?.startStep,
});

const FlowPhonePreview = ({
  flow,
  actions = [],
  entryStep = null,   // jump straight to this step instead of triggering the flow
  seed = 0,           // bump to re-run; edits alone must not restart the chat
  onExpand,
  size = "compact",   // "compact" (editor column) | "full" (modal)
}) => {
  const branding = useBranding();

  const [messages, setMessages] = useState([]);
  const [queue, setQueue] = useState([]);
  const [typing, setTyping] = useState(false);
  const [stepKey, setStepKey] = useState(null);
  const [endedReason, setEndedReason] = useState("");
  const [draft, setDraft] = useState("");
  const [ranSignature, setRanSignature] = useState("");

  const chatRef = useRef(null);
  const inputRef = useRef(null);
  /* The flow changes on every keystroke in the editor; the run must not.
     Read through a ref so only `seed` restarts the conversation. */
  const flowRef = useRef(flow);
  flowRef.current = flow;

  const actionByKey = useMemo(
    () => Object.fromEntries(actions.map((a) => [a.key, a])), [actions],
  );

  const currentStep = stepKey ? stepByKey(flow, stepKey) : null;
  const running = !!stepKey;
  const dirty = !!messages.length && ranSignature !== runSignature(flow);

  /* ── Delivery ──────────────────────────────────────────────────────────────
     Messages land one at a time behind a typing indicator. This is not
     decoration: a flow that fires four bubbles at one option is unpleasant to
     receive, and a preview that stacks them instantly hides that. */
  useEffect(() => {
    if (!queue.length) { setTyping(false); return undefined; }
    const [head, ...rest] = queue;
    const isMsg = head.kind === "msg";
    setTyping(isMsg);
    const delay = isMsg
      ? Math.min(1200, 340 + String(head.text || "").length * 7)
      : 260;
    const t = setTimeout(() => {
      setMessages((prev) => [...prev, { ...head, id: uid(), dir: "in", ts: new Date() }]);
      setQueue(rest);
    }, delay);
    return () => clearTimeout(t);
  }, [queue]);

  useEffect(() => {
    const el = chatRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, typing]);

  const apply = useCallback((result) => {
    setQueue((q) => [...q, ...result.items]);
    setStepKey(result.step);
    setEndedReason(result.step ? "" : (result.reason || "flow_complete"));
  }, []);

  /* ── Run ──────────────────────────────────────────────────────────────────
     A jump starts mid-flow with no trigger; an ordinary run opens the way a
     customer would open it — by sending the first keyword. Seeing the trigger
     work is half of what the admin came here to check. */
  const run = useCallback((from) => {
    const f = flowRef.current;
    const now = new Date();
    setQueue([]);
    setTyping(false);
    setDraft("");
    setEndedReason("");
    setRanSignature(runSignature(f));

    if (from) {
      setMessages([]);
      apply(advance(f, from));
      return;
    }

    const opener = f.triggerType === "keyword"
      ? (f.keywords || []).find((k) => String(k || "").trim())
      : f.triggerType === "first_message" ? "Hi" : null;

    // A keyword flow with no keywords can never start. Opening the chat with a
    // menu the customer has no way to summon would preview a lie.
    if (f.triggerType === "keyword" && !opener) {
      setMessages([]);
      setStepKey(null);
      setEndedReason("no_keywords");
      return;
    }

    setMessages(opener
      ? [{ id: uid(), kind: "msg", dir: "out", text: opener, ts: now }]
      : []);
    apply(advance(f, f.startStep || f.steps?.[0]?.key));
  }, [apply]);

  /* `seed` alone — depending on `entryStep` or `run` would restart the chat
     while the admin is still typing the message they want to test. */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { run(entryStep); }, [seed]);

  /* ── Inbound ─────────────────────────────────────────────────────────────── */
  const send = useCallback((raw) => {
    const text = String(raw ?? "").trim();
    if (!text) return;
    const f = flowRef.current;

    setMessages((prev) => [...prev, { id: uid(), kind: "msg", dir: "out", text, ts: new Date() }]);
    setDraft("");

    if (stepKey) {
      apply(replyTo(f, stepKey, text, { actionByKey }));
      return;
    }

    /* Outside a flow the server looks for a trigger. Nothing matching is not a
       bug — it is the AI agent's turn — but it is worth saying out loud, since
       an admin testing a keyword flow reads silence as broken. */
    const isFirstMessage = !messages.some((m) => m.dir === "out");
    if (matchTrigger(f, text, { isFirstMessage })) {
      apply(advance(f, f.startStep || f.steps?.[0]?.key));
      return;
    }
    setQueue((q) => [...q, {
      kind: "note",
      tone: "info",
      text: f.triggerType === "manual"
        ? "This flow never starts on its own — only a manual start puts a contact into it."
        : f.triggerType === "first_message"
          ? "This flow starts only on a contact's very first message."
          : "No keyword matched, so the flow does not start — the AI agent would answer this.",
    }]);
  }, [stepKey, actionByKey, apply, messages]);

  /* ── Chrome ──────────────────────────────────────────────────────────────── */
  const title = branding?.brandName || "Your business";
  const initial = title.trim().charAt(0).toUpperCase() || "W";
  /* The header avatar is a 32px circle, which is what a favicon is drawn for.
     The full logo is a wordmark — cropped to a circle it reads as a smear, so
     it is not a fallback here; the initial is. */
  const avatar = branding?.faviconUrl || branding?.pwaIconUrl || null;

  const statusText = (() => {
    if (queue.length || typing) return "sending…";
    if (running) return `on ${stepKey}`;
    switch (endedReason) {
      case "flow_complete": return "flow ended";
      case "terminal_step": return "flow ended — last step has no options";
      case "missing_step": return "stopped — missing step";
      case "step_gone": return "stopped — step deleted";
      case "empty_step": return "stopped — empty message";
      case "no_keywords": return "no keywords — add one to trigger this flow";
      default: return "not running";
    }
  })();
  const statusTone = running ? "live"
    : ["missing_step", "step_gone", "empty_step", "no_keywords"].includes(endedReason) ? "bad"
      : "done";

  const isFull = size === "full";

  return (
    <div className="tw-flex tw-flex-col tw-items-center tw-gap-2.5">
      <div className={isFull ? FRAME_FULL : FRAME}>
        <div className={isFull ? SCREEN_FULL : SCREEN}>
          <div className={STATUS_BAR}>
            <span>{clock(new Date())}</span>
            <span className="tw-flex tw-items-center tw-gap-[5px]">
              <span className="tw-flex tw-h-2 tw-items-end tw-gap-px">
                <i className={`${SIG_BAR} tw-h-[3px]`} />
                <i className={`${SIG_BAR} tw-h-[5px]`} />
                <i className={`${SIG_BAR} tw-h-1.5`} />
                <i className={`${SIG_BAR} tw-h-2`} />
              </span>
              <span className="tw-h-0 tw-w-0 tw--translate-y-px tw-border-[5px] tw-border-solid tw-border-transparent tw-border-t-[var(--wa-client-on-head)]" />
              <span className={BATT} />
            </span>
          </div>

          <header className={TOP}>
            <ChevronLeft size={20} className="tw-shrink-0 tw-opacity-95" />
            {avatar
              ? <img className={AVATAR} src={avatar} alt="" />
              : (
                <span className={`${AVATAR} tw-flex tw-items-center tw-justify-center tw-text-[0.85rem] tw-font-bold`}>
                  {initial}
                </span>
              )}
            <span className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-leading-[1.25]">
              <span className="tw-truncate tw-text-[0.82rem] tw-font-semibold">{title}</span>
              <span className="tw-text-[0.63rem] tw-opacity-85">online</span>
            </span>
            <Video size={19} />
            <Phone size={17} />
            <MoreVertical size={18} />
          </header>

          <div className={CHAT} ref={chatRef}>
            <div className={DAY_PILL}>TODAY</div>
            <div className={ENC_PILL}>
              <Lock size={10} /> Messages are end-to-end encrypted.
            </div>

            {messages.map((m) => (m.kind === "note" ? (
              <div key={m.id} className={NOTE_TONE[m.tone] || NOTE_TONE.info}>
                {m.tone === "action" ? <Zap size={11} />
                  : m.tone === "info" ? <Info size={11} />
                    : <AlertTriangle size={11} />}
                <span>{m.text}</span>
              </div>
            ) : (
              <div
                key={m.id}
                className={`tw-mb-[5px] tw-flex ${m.dir === "out" ? "tw-justify-end" : "tw-justify-start"}`}
              >
                <div className={m.dir === "out" ? BUBBLE_OUT : BUBBLE_IN}>
                  <span className={BUBBLE_TEXT}>{formatWa(m.text)}</span>
                  <span className={BUBBLE_META}>
                    {clock(m.ts)}
                    {m.dir === "out" && (
                      <CheckCheck size={13} className="tw-text-[var(--ext-whatsapp-tick)]" />
                    )}
                  </span>
                </div>
              </div>
            )))}

            {typing && (
              <div className="tw-mb-[5px] tw-flex tw-justify-start">
                <div className={TYPING_BUBBLE}>
                  <i className={TYPING_DOT} />
                  <i className={`${TYPING_DOT} [animation-delay:0.18s]`} />
                  <i className={`${TYPING_DOT} [animation-delay:0.36s]`} />
                </div>
              </div>
            )}
          </div>

          <footer className={COMPOSER}>
            <div className={COMPOSER_FIELD}>
              <Smile size={19} />
              <input
                ref={inputRef}
                className={COMPOSER_INPUT}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); send(draft); } }}
                placeholder="Message"
                aria-label="Reply as the customer"
              />
              <Paperclip size={18} />
              <Camera size={18} />
            </div>
            <button
              type="button"
              className={SEND_BTN}
              onClick={() => (draft.trim() ? send(draft) : inputRef.current?.focus())}
              aria-label={draft.trim() ? "Send" : "Voice message"}
            >
              {draft.trim() ? <Send size={17} /> : <Mic size={18} />}
            </button>
          </footer>
        </div>
      </div>

      {/* Outside the frame on purpose — the phone shows only what the contact
          would see, and these are the simulator talking. */}
      <div className="tw-flex tw-w-full tw-max-w-[340px] tw-flex-col tw-gap-[7px]">
        <div className="tw-flex tw-items-center tw-gap-1.5">
          <span className={STATE_TONE[statusTone]}>{statusText}</span>
          <span className="tw-flex-1" />
          {onExpand && (
            <button type="button" className={TOOL_BTN} onClick={onExpand} title="Open full size">
              <Maximize2 size={13} /> Expand
            </button>
          )}
          <button
            type="button"
            className={TOOL_BTN}
            onClick={() => run(entryStep)}
            title="Start the conversation again"
          >
            <RotateCcw size={13} /> Restart
          </button>
        </div>

        {/* Tapping beats typing "3" forty times, and it is the same code path as
            a typed reply — the chips send text, they do not shortcut the match. */}
        {!!currentStep?.options?.length && (
          <div className="tw-flex tw-flex-wrap tw-gap-[5px]">
            {currentStep.options.map((o, i) => (
              <button
                key={i}
                type="button"
                className={CHIP_ON}
                onClick={() => send(String(i + 1))}
                title={`Reply “${i + 1}”`}
              >
                <b>{i + 1}</b> {o.label || `Option ${i + 1}`}
              </button>
            ))}
            <button
              type="button"
              className={CHIP_OFF}
              onClick={() => send("something else")}
              title="See what an unmatched reply gets"
            >
              Wrong answer
            </button>
          </div>
        )}

        {dirty && (
          <div className="tw-flex tw-items-center tw-gap-1 tw-text-[0.66rem] tw-leading-[1.4] tw-text-warning-fg [&>svg]:tw-shrink-0">
            <AlertTriangle size={11} /> Edited since this run — restart to test the new version.
          </div>
        )}
      </div>
    </div>
  );
};

export default FlowPhonePreview;
